import React, { useCallback, useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { Camera, AlertTriangle, Keyboard, Zap, ZapOff } from "lucide-react";
import { resolveCameraPermissionState } from "../lib/cameraPermission";

/**
 * Continuous, embedded barcode scanner with optional torch (flashlight) support.
 * - Renders within its parent container (NOT fullscreen).
 * - Keeps the camera running continuously.
 * - Calls onDetected(barcode) on each new detection, with cooldown so the same
 *   barcode isn't fired repeatedly while held in front of the camera.
 * - Torch modes: "auto" | "on" | "off". Default "auto" when supported.
 *
 * Props:
 *   onDetected: (barcode: string) => void
 *   paused: boolean — when true, ignore detections (camera stays on)
 *   className: string
 */
export function LiveScanner({ onDetected, paused = false, className = "" }) {
  const videoRef = useRef(null);
  const controlsRef = useRef(null);
  const readerRef = useRef(null);
  const lastDetectionRef = useRef({ code: null, ts: 0 });
  const pausedRef = useRef(paused);
  const trackRef = useRef(null);
  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(true);
  const [manualMode, setManualMode] = useState(false);
  const [manualValue, setManualValue] = useState("");
  // permState — distinguishes the THREE meaningful camera permission
  // states so we never show "denied" before the user has even been
  // asked. Set by ``resolveCameraPermissionState`` on mount, then kept
  // live via ``permissions.query().onchange`` where the API supports
  // it, and finally bumped to "denied" by the getUserMedia catch
  // below when an actual NotAllowedError comes back from the platform.
  //
  //   "checking" — initial; transient, blocks start()
  //   "prompt"   — Permissions API says we may ask; start() will run
  //                and surface the native dialog; UI shows the
  //                friendly hint ("Tap Allow when prompted")
  //   "granted"  — start() runs, no overlay message
  //   "denied"   — start() is skipped; UI shows the denied callout
  const [permState, setPermState] = useState("checking");
  // Torch state:
  //   torchSupported — true once we confirm the active track exposes a
  //     `torch` capability. Required because most desktop cameras and many
  //     mobile front cameras do not.
  //   torchMode      — user-selected mode. Persisted to sessionStorage so a
  //     stray re-mount doesn't snap the flashlight off mid-shift.
  //   torchOn        — actual physical state of the LED (mirrors what we
  //     last asked the track for). Auto mode flips this internally.
  //   torchError     — non-null when applyConstraints() throws. Surfaces
  //     a small notice in the UI; we never crash the scanner over torch.
  const [torchSupported, setTorchSupported] = useState(false);
  const [torchMode, setTorchMode] = useState(() => {
    try { return sessionStorage.getItem("shelflife-torch-mode") || "auto"; }
    catch (_) { return "auto"; }
  });
  const [torchOn, setTorchOn] = useState(false);
  const [torchError, setTorchError] = useState(null);

  useEffect(() => { pausedRef.current = paused; }, [paused]);
  useEffect(() => {
    try { sessionStorage.setItem("shelflife-torch-mode", torchMode); } catch (_) { /* private mode */ }
  }, [torchMode]);

  // Resolve the current camera permission state on mount, and keep
  // listening for live changes (e.g. user toggling permission in
  // Safari Website Settings while the page is open). Critical for the
  // "false denied" bugfix: this is what stops the UI from claiming
  // permission was refused before the user has actually been asked.
  useEffect(() => {
    let cancelled = false;
    let permissionStatus = null;
    (async () => {
      const initial = await resolveCameraPermissionState();
      if (cancelled) return;
      setPermState(initial);
      // Subscribe to live changes if the platform exposes them.
      // Safari iOS / older WebViews skip this branch silently —
      // their fallback is the existing getUserMedia catch below.
      if (
        typeof navigator !== "undefined" &&
        navigator.permissions &&
        typeof navigator.permissions.query === "function"
      ) {
        try {
          permissionStatus = await navigator.permissions.query({ name: "camera" });
          if (cancelled) return;
          permissionStatus.onchange = () => {
            if (cancelled) return;
            const s = permissionStatus.state;
            if (s === "granted" || s === "denied" || s === "prompt") {
              setPermState(s);
            }
          };
        } catch (_) {
          // Safari iOS throws — nothing to do, initial resolution
          // already defaulted us to "prompt".
        }
      }
    })();
    return () => {
      cancelled = true;
      if (permissionStatus) {
        try { permissionStatus.onchange = null; } catch (_) { /* noop */ }
      }
    };
  }, []);

  // Apply the torch state to the active MediaStreamTrack. Resolves true on
  // success, false on failure. We never propagate the error — torch is a
  // strict best-effort enhancement; the scanner must keep working.
  const applyTorch = useCallback(async (on) => {
    const t = trackRef.current;
    if (!t) return false;
    try {
      await t.applyConstraints({ advanced: [{ torch: !!on }] });
      setTorchError(null);
      return true;
    } catch (e) {
      // Some Android Chrome builds throw when the camera is in the middle
      // of an internal restart. Don't surface that as a fatal error —
      // just remember it for the small inline notice.
      setTorchError(e && e.message ? e.message : "Torch not available");
      return false;
    }
  }, []);

  // Detect torch support once the video track is live. We re-check
  // whenever `starting` flips false so a re-init (e.g. switching to the
  // rear camera after a permission prompt) gets fresh capabilities.
  useEffect(() => {
    if (manualMode || starting) return;
    const v = videoRef.current;
    const stream = v && v.srcObject;
    if (!stream || typeof stream.getVideoTracks !== "function") return;
    const tracks = stream.getVideoTracks();
    if (!tracks.length) return;
    trackRef.current = tracks[0];
    let supported = false;
    try {
      const caps = typeof tracks[0].getCapabilities === "function" ? tracks[0].getCapabilities() : {};
      supported = !!(caps && caps.torch);
    } catch (_) {
      supported = false;
    }
    setTorchSupported(supported);
    // Coming out of an unsupported state — force the lamp off so we
    // don't leave stale state if the user later moves to a supported
    // device.
    if (!supported) setTorchOn(false);
  }, [manualMode, starting]);

  // Hard-set torch when user is in "on" or "off" mode.
  useEffect(() => {
    if (!torchSupported || manualMode) return;
    if (torchMode === "on") {
      applyTorch(true).then((ok) => setTorchOn(ok));
    } else if (torchMode === "off") {
      applyTorch(false).then(() => setTorchOn(false));
    }
  }, [torchMode, torchSupported, manualMode, applyTorch]);

  // Auto-brightness loop. Samples a small (64×36) downscaled frame from
  // the live video every ~1.5s, computes mean luminance, and toggles
  // torch with hysteresis to avoid flicker:
  //    avg < 55 → turn on
  //    avg > 95 → turn off
  // Only runs while torchMode === "auto" and the scanner isn't paused.
  // Heuristic only — works well enough for stock-room shelves but a
  // proper solution would use the AmbientLight Sensor API (not yet
  // shipping universally on iOS Safari as of 2026-05).
  useEffect(() => {
    if (!torchSupported || torchMode !== "auto" || manualMode) return;
    let cancelled = false;
    let currentTorch = false;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext && canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return;
    canvas.width = 64;
    canvas.height = 36;

    const tick = () => {
      if (cancelled) return;
      const v = videoRef.current;
      if (!v || v.videoWidth === 0 || pausedRef.current) {
        if (!cancelled) setTimeout(tick, 1500);
        return;
      }
      try {
        ctx.drawImage(v, 0, 0, 64, 36);
        const data = ctx.getImageData(0, 0, 64, 36).data;
        let sum = 0;
        const len = data.length;
        for (let i = 0; i < len; i += 4) {
          sum += data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
        }
        const avg = sum / (64 * 36);
        let nextTorch = currentTorch;
        if (!currentTorch && avg < 55) nextTorch = true;
        else if (currentTorch && avg > 95) nextTorch = false;
        if (nextTorch !== currentTorch) {
          currentTorch = nextTorch;
          applyTorch(nextTorch).then((ok) => { if (ok) setTorchOn(nextTorch); });
        }
      } catch (_) {
        // CORS / not-ready / detached — ignore and try again next tick.
      }
      if (!cancelled) setTimeout(tick, 1500);
    };

    const kick = setTimeout(tick, 1200); // give the stream a moment to settle
    return () => {
      cancelled = true;
      clearTimeout(kick);
      // Leaving auto: lamp off so the next mode starts from a known state.
      applyTorch(false).then(() => setTorchOn(false));
    };
  }, [torchMode, torchSupported, manualMode, applyTorch]);

  useEffect(() => {
    if (manualMode) return;
    // Permission gate (bugfix 2026-06-17):
    //   * "checking" — Permissions API hasn't resolved yet; wait. The
    //     effect will re-run as soon as ``permState`` flips because it
    //     is in the deps array.
    //   * "denied"   — DO NOT call getUserMedia. The denied overlay is
    //     rendered below; the user must enable camera access in their
    //     browser settings to recover.
    //   * "prompt" / "granted" — proceed to start the camera. In the
    //     prompt case the native browser dialog will appear as part of
    //     the getUserMedia call.
    if (permState === "checking" || permState === "denied") return;
    let cancelled = false;
    const reader = new BrowserMultiFormatReader();
    readerRef.current = reader;

    const handle = (result) => {
      if (cancelled || pausedRef.current || !result) return;
      const code = result.getText();
      const now = Date.now();
      // De-duplicate: ignore same code within 2 seconds
      if (lastDetectionRef.current.code === code && (now - lastDetectionRef.current.ts) < 2000) return;
      lastDetectionRef.current = { code, ts: now };
      onDetected(code);
    };

    const start = async () => {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError("Camera not supported on this device. Use manual entry.");
        setStarting(false);
        return;
      }
      try {
        const constraints = {
          audio: false,
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } },
        };
        const controls = await reader.decodeFromConstraints(
          constraints,
          videoRef.current,
          (result) => handle(result),
        );
        controlsRef.current = controls;
        if (!cancelled) {
          setStarting(false);
          // Successful start implies permission was either already
          // granted or just granted via the native prompt. Promote
          // permState so the friendly hint overlay disappears.
          if (permState === "prompt") setPermState("granted");
        }
      } catch (e) {
        if (cancelled) return;
        try {
          const devices = await BrowserMultiFormatReader.listVideoInputDevices();
          if (!devices || !devices.length) throw e;
          const rear = devices.find((d) => /back|rear|environment/i.test(d.label)) || devices[devices.length - 1];
          const controls = await reader.decodeFromVideoDevice(
            rear.deviceId,
            videoRef.current,
            (result) => handle(result),
          );
          controlsRef.current = controls;
          if (!cancelled) {
            setStarting(false);
            // Same promotion path as the primary branch: successful
            // start means permission is now granted.
            if (permState === "prompt") setPermState("granted");
          }
        } catch (e2) {
          const name = (e2 && e2.name) || (e && e.name) || "";
          const msg = (e2 && e2.message) || (e && e.message) || "Unable to access camera";
          if (name === "NotAllowedError" || /permission/i.test(msg)) {
            // Real denial — by the time the browser surfaces
            // NotAllowedError, the native prompt has either been
            // shown and rejected, or the user previously denied.
            // Transition permState; the overlay below renders the
            // approved denial copy. We deliberately do NOT call
            // setError here so the message comes from a single source.
            setPermState("denied");
          } else if (name === "NotFoundError") {
            setError("No camera detected. Use manual entry.");
          } else if (name === "NotReadableError") {
            setError("Camera is in use by another app.");
          } else {
            setError(msg);
          }
          setStarting(false);
        }
      }
    };

    start();
    return () => {
      cancelled = true;
      // Best-effort torch shutdown so the LED doesn't stay on after the
      // component unmounts (e.g. navigating away from /scan).
      try { trackRef.current?.applyConstraints?.({ advanced: [{ torch: false }] }); } catch (_) { /* noop */ }
      try { controlsRef.current?.stop(); } catch (_) { /* noop */ }
      trackRef.current = null;
    };
  }, [manualMode, onDetected, permState]);

  const submitManual = (e) => {
    e.preventDefault();
    const v = manualValue.trim();
    if (v) {
      onDetected(v);
      setManualValue("");
    }
  };

  const TorchPill = () => {
    // Three-state pill. Inactive segments are translucent so the live
    // video stays visible. The currently-active segment is inverted.
    const seg = (mode, label, testid) => (
      <button
        key={mode}
        type="button"
        onClick={() => setTorchMode(mode)}
        className={`h-7 px-2 text-[10px] uppercase tracking-wider font-mono transition-colors ${
          torchMode === mode ? "bg-ink text-white" : "bg-white/85 text-ink hover:bg-white"
        }`}
        data-testid={testid}
        aria-pressed={torchMode === mode}
      >
        {label}
      </button>
    );
    return (
      <div className="absolute top-2 right-2 z-10 pointer-events-auto" data-testid="torch-pill">
        <div className="flex rounded-sm overflow-hidden border border-ink/30 shadow-sm">
          {seg("auto", "Auto", "torch-mode-auto")}
          {seg("on", "On", "torch-mode-on")}
          {seg("off", "Off", "torch-mode-off")}
        </div>
        <div className="mt-1 flex items-center justify-end gap-1 text-[10px] font-mono uppercase tracking-wider text-white drop-shadow">
          {torchOn ? <Zap size={11} className="text-shelf-attention" /> : <ZapOff size={11} className="text-white/70" />}
          <span className="bg-black/55 px-1.5 py-0.5 rounded-sm" data-testid="torch-status">
            {torchOn ? "Lamp on" : "Lamp off"}
          </span>
        </div>
        {torchError && (
          <div className="mt-1 px-1.5 py-0.5 bg-shelf-urgent-bg/95 border border-shelf-urgent-bd text-shelf-urgent text-[10px] font-mono rounded-sm max-w-[200px] truncate" data-testid="torch-error">
            {torchError}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`relative w-full h-full bg-black overflow-hidden ${className}`} data-testid="live-scanner">
      {!manualMode && (
        <video
          ref={videoRef}
          className="w-full h-full object-cover"
          autoPlay
          muted
          playsInline
          // eslint-disable-next-line react/no-unknown-property
          webkit-playsinline="true"
        />
      )}
      {!manualMode && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="border-2 border-brand rounded-sm w-3/4 max-w-[260px] h-24" />
        </div>
      )}
      {!manualMode && (
        <div className="absolute top-2 left-2 right-32 flex items-center justify-between gap-2 pointer-events-none">
          <div className="px-2 py-1 bg-black/55 rounded-sm text-white text-[10px] font-mono uppercase tracking-wider">
            {paused
              ? "Scan paused"
              : permState === "denied"
                ? "Camera blocked"
                : permState === "prompt" || permState === "checking"
                  ? "Awaiting permission"
                  : starting
                    ? "Starting…"
                    : "Scanning"}
          </div>
          {/* Other non-permission errors (NotFoundError / NotReadableError /
              unsupported device) still surface through ``error``; permission
              UI is rendered separately as a full callout below so users
              read the correct guidance based on permState. */}
          {error && (
            <div
              className="px-2 py-1 bg-shelf-urgent-bg/95 border border-shelf-urgent-bd text-shelf-urgent rounded-sm text-[10px] font-mono flex items-center gap-1 max-w-[60%]"
              data-testid="live-scanner-error-badge"
            >
              <AlertTriangle size={11} className="shrink-0" />
              <span className="truncate">{error}</span>
            </div>
          )}
        </div>
      )}
      {/* Camera-permission overlays. These render IN PLACE of the
          previous one-size-fits-all "Camera permission denied" badge.

          * permState === "prompt" / "checking":
              The user has not yet been asked or the resolver hasn't
              returned. We show a friendly hint instead of the
              alarming denied error.
          * permState === "denied":
              Real refusal — surfaced after navigator.permissions or
              getUserMedia rejected. We point the user at the recovery
              path (browser/Safari Website Settings).
          The bug fix (2026-06-17) replaces the prior conflation where
          a fast NotAllowedError was rendered as "denied" before the
          native prompt had even appeared. */}
      {!manualMode && (permState === "prompt" || permState === "checking") && (
        <div
          className="absolute inset-x-2 bottom-12 flex items-center gap-2 px-3 py-2 bg-black/65 text-white rounded-sm pointer-events-none"
          data-testid="live-scanner-permission-prompt"
        >
          <Camera size={14} className="shrink-0" />
          <div className="font-mono text-[11px] leading-snug">
            Camera access required. Tap <strong>Allow</strong> when your
            browser asks for camera permission.
          </div>
        </div>
      )}
      {!manualMode && permState === "denied" && (
        <div
          className="absolute inset-x-2 bottom-12 flex items-start gap-2 px-3 py-2 bg-shelf-urgent-bg/95 border border-shelf-urgent-bd text-shelf-urgent rounded-sm pointer-events-none"
          data-testid="live-scanner-permission-denied"
        >
          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
          <div className="font-mono text-[11px] leading-snug">
            Camera access denied. Enable camera access in your browser&apos;s
            site settings — on iPhone, open Safari Settings → Websites →
            Camera. You can still use <strong>Manual</strong> entry below.
          </div>
        </div>
      )}
      {!manualMode && !starting && (
        torchSupported ? <TorchPill /> : (
          <div
            className="absolute top-2 right-2 z-10 px-2 py-1 bg-black/55 rounded-sm text-white/85 text-[10px] font-mono uppercase tracking-wider max-w-[180px] pointer-events-none"
            data-testid="torch-unsupported-note"
          >
            Flashlight not available on this device.
          </div>
        )
      )}
      {!manualMode && (
        <button
          onClick={() => setManualMode(true)}
          className="absolute bottom-2 right-2 px-3 py-1.5 bg-white/90 text-ink rounded-sm font-mono text-[10px] uppercase tracking-wider flex items-center gap-1 pointer-events-auto"
          data-testid="live-scanner-manual-toggle"
        >
          <Keyboard size={12} /> Manual
        </button>
      )}
      {manualMode && (
        <form onSubmit={submitManual} className="absolute inset-0 bg-paper p-4 flex flex-col justify-center" data-testid="live-scanner-manual-form">
          <div className="data-sku mb-2">Type or paste the barcode</div>
          <input
            type="text"
            inputMode="numeric"
            autoFocus
            value={manualValue}
            onChange={(e) => setManualValue(e.target.value)}
            className="w-full h-12 px-4 border border-border rounded-sm bg-white font-mono text-base mb-3"
            placeholder="e.g. 8410076472113"
            data-testid="live-scanner-manual-input"
          />
          <div className="flex gap-2">
            <button type="submit" className="btn-primary flex-1 h-11" disabled={!manualValue.trim()} data-testid="live-scanner-manual-submit">Find</button>
            <button type="button" onClick={() => setManualMode(false)} className="btn-secondary flex-1 h-11" data-testid="live-scanner-back-to-camera">
              <Camera size={16} /> Camera
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default LiveScanner;
